import { parse, isValid, differenceInYears } from "date-fns";
import * as z from "zod/v4-mini";

import { unacceptableInputs } from "@/lib/utils/offensive-words";
import { trimIfNameField, trimNameFields } from "@/lib/utils/trim-name-fields";
import type { GuarantorData } from "@/types/scheduler-machine";

const DATE_FORMAT = "MM/dd/yyyy";

const guarantorDataSchema = z.object({
  firstName: z.string().check(
    z.regex(/^[a-z]+$/i, "firstNameRequired"),
    z.maxLength(15, { message: "firstNameMax" }),
    z.refine(
      (val) => unacceptableInputs([{ name: "firstName", value: val }]).length === 0,
      "offensiveInput",
    ),
  ),
  lastName: z.string().check(
    z.regex(/^[a-z]+$/i, "lastNameRequired"),
    z.refine(
      (val) => unacceptableInputs([{ name: "lastName", value: val }]).length === 0,
      "offensiveInput",
    ),
  ),
  dateOfBirth: z.string().check(
    z.minLength(1, "dobRequired"),
    z.refine((val) => isValid(parse(val, DATE_FORMAT, new Date())), "dobFormat"),
    z.refine((val) => {
      const parsed = parse(val, DATE_FORMAT, new Date());
      if (!isValid(parsed)) return true;
      const years = differenceInYears(new Date(), parsed);
      return years >= 18 && years <= 150;
    }, "dobAgeG"),
  ),
  guarantorZipCode: z
    .string()
    .check(z.minLength(1, "zipRequired"), z.regex(/^\d{5}$/, "zipInvalid")),
  email: z.pipe(z.string().check(z.minLength(1, "emailRequired")), z.email("emailInvalid")),
  guarantorPhoneNumber: z
    .string()
    .check(z.minLength(1, "phoneRequired"), z.regex(/^\(\d{3}\) \d{3}-\d{4}$/, "phoneInvalid")),
  relationshipToPatient: z.string().check(z.minLength(1, "relationshipRequired")),
});

type GuarantorDataErrors = Partial<Record<keyof GuarantorData, string>>;
type SchemaField =
  | "firstName"
  | "lastName"
  | "dateOfBirth"
  | "guarantorZipCode"
  | "email"
  | "guarantorPhoneNumber"
  | "relationshipToPatient";

export function validateField(field: SchemaField, value: unknown): boolean {
  return guarantorDataSchema.shape[field].safeParse(trimIfNameField(field, value)).success;
}

export function validateGuarantorData(data: unknown): {
  success: boolean;
  errors: GuarantorDataErrors;
  data?: GuarantorData;
} {
  const normalizedData =
    data && typeof data === "object" ? trimNameFields(data as Record<string, unknown>) : data;
  const result = guarantorDataSchema.safeParse(normalizedData);

  if (result.success) {
    return { success: true, errors: {}, data: result.data };
  }

  const fieldErrors = z.flattenError(result.error).fieldErrors;
  const errors: GuarantorDataErrors = {};

  for (const [key, messages] of Object.entries(fieldErrors)) {
    if (messages && messages.length > 0) {
      errors[key as keyof GuarantorData] = messages[0];
    }
  }

  return { success: false, errors };
}
